import { computed, onUnmounted, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

import { useRoute, useState } from 'nuxt/app'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { DeferPriority } from '@common/core-layer/composables/deferPriority'
import { onDeferredMounted } from '@common/core-layer/composables/onDeferredMounted'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { useGroups } from '@communities/groups-layer/composables/useGroups'
import { isTerminalGroupsListPage } from '@communities/groups-layer/utils/groups-list-pagination.utils'

import type { IGroup } from '@communities/groups-layer/types/types'
import type { InfiniteData } from '@tanstack/vue-query'

/** Matches default page size in `fetchGroupsInfiniteQuery`. */
export const JOINED_GROUPS_LIST_PAGE_LIMIT = 20

const IDLE_CALLBACK_TIMEOUT_MS = 2000
const IDLE_FALLBACK_DELAY_MS = 80

export const CP_JOINED_GROUPS_LIST_DRAINED_STATE_KEY = 'cp-joined-groups-list-drained'

/** Client Portal communities hub and embedded layer routes (`/communities/...`). */
export function isCommunitiesPath(path: string): boolean {
  return path.startsWith('/communities')
}

export function isJoinedGroupsListCacheComplete(data: InfiniteData<IGroup[]> | undefined, limit = JOINED_GROUPS_LIST_PAGE_LIMIT): boolean {
  const pages = data?.pages
  if (!pages?.length) return false
  const lastPage = pages[pages.length - 1]
  return isTerminalGroupsListPage(lastPage.length, limit)
}

function waitForNextIdle(signal: AbortSignal): Promise<void> {
  if (!import.meta.client || signal.aborted) {
    return Promise.resolve()
  }

  return new Promise(resolve => {
    let settled = false
    let idleId: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const finish = () => {
      if (settled) return
      settled = true
      signal.removeEventListener('abort', onAbort)
      resolve()
    }

    const onAbort = () => {
      if (idleId !== undefined && typeof window.cancelIdleCallback !== 'undefined') {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
      finish()
    }

    signal.addEventListener('abort', onAbort, { once: true })

    if (typeof window.requestIdleCallback !== 'undefined') {
      idleId = window.requestIdleCallback(() => finish(), { timeout: IDLE_CALLBACK_TIMEOUT_MS })
    } else {
      timeoutId = setTimeout(finish, IDLE_FALLBACK_DELAY_MS)
    }
  })
}

/**
 * Drains the default joined-groups infinite query during idle time so
 * `fetchGroupBySlugQuery` placeholderData can read from the list cache.
 */
export function useCPJoinedGroupsListPrefetch(isAuthPage: MaybeRefOrGetter<boolean>) {
  if (import.meta.server) {
    return
  }

  const { store: authStore } = useAuth()
  const location = useLocation()
  const route = useRoute()
  const groups = useGroups()

  const drained = useState(CP_JOINED_GROUPS_LIST_DRAINED_STATE_KEY, () => false)
  const allowListQuery = ref(false)
  const drainInFlight = ref(false)
  const prefetchAbortController = new AbortController()

  // Hard runtime gate: only prefetch for authenticated, non-auth pages with a resolved location.
  const prefetchAllowed = computed(() => authStore.isLoggedIn && !!location.locationId.value && !toValue(isAuthPage))
  // Session identity used to reset prefetch state on logout/login or location switches.
  const prefetchSessionKey = computed(() => (authStore.isLoggedIn ? location.locationId.value || null : null))

  const listQuery = groups.getGroups(() => ({
    // Keep the query dormant until we explicitly start draining.
    enabled: allowListQuery.value && prefetchAllowed.value,
    limit: JOINED_GROUPS_LIST_PAGE_LIMIT,
  }))

  const markDrainedIfCacheComplete = () => {
    // We treat cache as complete when last page is terminal (< limit OR > limit).
    if (!isJoinedGroupsListCacheComplete(listQuery.data.value as InfiniteData<IGroup[]> | undefined)) {
      return false
    }
    drained.value = true
    return true
  }

  const drainJoinedGroupsList = async (signal: AbortSignal) => {
    if (signal.aborted || drained.value) return

    if (markDrainedIfCacheComplete()) {
      return
    }

    if (!prefetchAllowed.value) {
      return
    }

    // Open the query gate only when we are ready to run the idle drain loop.
    allowListQuery.value = true

    while (!signal.aborted && !drained.value) {
      // Pull one page at a time during idle slices to avoid competing with critical UI work.
      await waitForNextIdle(signal)
      if (signal.aborted || drained.value) return

      if (markDrainedIfCacheComplete()) {
        return
      }

      if (listQuery.isFetching.value || listQuery.isFetchingNextPage.value) {
        // Let in-flight requests settle before issuing the next page request.
        continue
      }

      if (listQuery.isError.value) {
        console.warn('[useCPJoinedGroupsListPrefetch] joined groups list prefetch failed:', listQuery.error.value)
        return
      }

      if (listQuery.hasNextPage.value) {
        await listQuery.fetchNextPage()
        continue
      }

      // No next page and we have data -> consider this session fully drained.
      if (listQuery.data.value?.pages?.length) {
        drained.value = true
      }
      return
    }
  }

  const startJoinedGroupsListPrefetch = () => {
    if (drained.value || drainInFlight.value || prefetchAbortController.signal.aborted) {
      return
    }

    if (markDrainedIfCacheComplete()) {
      return
    }

    if (!prefetchAllowed.value) {
      return
    }

    drainInFlight.value = true
    void (async () => {
      try {
        await drainJoinedGroupsList(prefetchAbortController.signal)
      } finally {
        drainInFlight.value = false
      }
    })()
  }

  onDeferredMounted(
    () => {
      startJoinedGroupsListPrefetch()
    },
    {
      priority: DeferPriority.LOW,
      waitForLoad: true,
      signal: prefetchAbortController.signal,
    }
  )

  // `/communities/*`: start (and enable the list query) as soon as route + session gates are open.
  // Other routes: retry only when session gates flip true after deferred may have no-oped (not on first run).
  watch(prefetchSessionKey, (next, prev) => {
    if (next === prev) return
    // New auth/location session should re-run prefetch from a clean state.
    drained.value = false
    allowListQuery.value = false
  })

  watch(
    () => ({
      path: route.path,
      allowed: prefetchAllowed.value,
      drained: drained.value,
    }),
    (state, prev) => {
      if (state.drained || !state.allowed) {
        return
      }

      const onCommunities = isCommunitiesPath(state.path)
      if (onCommunities) {
        // On communities routes, allow the list query immediately (still guarded by session gate).
        allowListQuery.value = true
      }

      const sessionJustOpened = prev !== undefined && !prev.allowed && state.allowed
      if (onCommunities || sessionJustOpened) {
        startJoinedGroupsListPrefetch()
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    prefetchAbortController.abort()
  })
}
